import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import StackedMarkdownPrice from '../../components/stacked-markdown-price/stacked-markdown-price';
import { TimesLightIcon } from '../../shared/icons/icons';
import MapProductInstantPromoMessage from '../map-product-instant-promo-message/map-product-instant-promo-message';
import MinimumPriceMessage from '../minimum-price-message/minimum-price-message';
import Price from '../price/price';
import ProductDescription from '../product-description/product-description';
import ProductImage from '../product-image/product-image';

import { useMyAccount } from '../../shared/hooks/hooks';

import {
  getMapProductInstantSavingsInfo,
  getPriceInfo,
  getShouldShowInstantPromotionMessage,
  getShouldShowMarkdownPrice,
  getShouldShowMinimumPriceMessage
} from '../../shared/utils/product/product';

import './product-compare-preview-summary.scss';

function ProductComparePreviewSummary(props) {
  const {
    onClearProduct,
    product,
    hasInstantPromotions,
    hideImage,
    hideDescription,
    hideRemoveButton = false,
    className,
    seePriceInCartMessage = 'Price in cart'
  } = props;
  const { isLoggedIn } = useMyAccount();
  const isStaggered = Boolean(product?.rearProduct);
  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(product?.rearProduct);
  const showMarkdownPrice = getShouldShowMarkdownPrice(
    product,
    product?.totalQuantity
  );
  const { instantMapSavingsPerProduct = 0, totalMapSavings = 0 } =
    getMapProductInstantSavingsInfo(
      product,
      isLoggedIn,
      product?.totalQuantity
    );
  const mapShowMinimumPriceMessage =
    process.env.__FF_MAP_PRICE_STRIKE_THROUGH__ === 'true' &&
    instantMapSavingsPerProduct
      ? !isLoggedIn
      : true;
  const showMinimumPriceMessage =
    mapShowMinimumPriceMessage && getShouldShowMinimumPriceMessage(product);
  const showRearMinimumPriceMessage =
    mapShowMinimumPriceMessage &&
    getShouldShowMinimumPriceMessage(product?.rearProduct);
  const showInstantPromotionMessage = getShouldShowInstantPromotionMessage(
    instantMapSavingsPerProduct,
    product?.totalQuantity
  );
  const priceContainerStyleName = `${
    isStaggered ? 'price-container-staggered' : 'price-container'
  } ${
    hasInstantPromotions && !showInstantPromotionMessage
      ? 'price-container-no-promo'
      : ''
  }`;

  return (
    <div
      className={`${className || ''} product-compare-preview-summary`}
      styleName="container"
    >
      {product && !hideRemoveButton && (
        <Button
          icon={TimesLightIcon}
          onClick={() => onClearProduct(product)}
          styleName="delete-button"
        />
      )}
      {!hideImage && product && (
        <div
          className="product-compare-preview-summary__image-container"
          styleName="image-container"
        >
          <ProductImage
            height={137}
            product={product}
            styleName="product-image"
            width={114}
          />
        </div>
      )}
      {!hideDescription && product && (
        <div
          className={
            !hideImage
              ? 'product-compare-preview-summary__description'
              : 'product-compare-preview-summary__description-bordered'
          }
          styleName={!hideImage ? 'description' : 'description-bordered'}
        >
          <ProductDescription
            brand={product?.brand}
            name={product?.name}
            styleName="product-description"
          />
          <div styleName={priceContainerStyleName}>
            <div>
              {showInstantPromotionMessage && isStaggered && (
                <MapProductInstantPromoMessage
                  instantMapSavingsPerProduct={instantMapSavingsPerProduct}
                  showShortDescription
                  totalMapSavings={totalMapSavings}
                />
              )}
              {isStaggered && (
                <div
                  className="product-compare-preview-summary__price-label"
                  styleName="price-label"
                >
                  Front
                </div>
              )}
              {showMarkdownPrice && (
                <StackedMarkdownPrice
                  hideInstantPromo={isStaggered}
                  product={product}
                  totalQuantity={product?.totalQuantity}
                />
              )}

              <Price
                {...priceInfo}
                isPriceForEach
                seePriceInCartMessage={seePriceInCartMessage}
                styleName="price"
                totalQuantity={product?.totalQuantity}
                unitName={product?.unitName}
                variant="H3"
              />

              {showMinimumPriceMessage &&
                !priceInfo?.instantPromotionOriginalRetailValue && (
                  <MinimumPriceMessage
                    message={product?.mapDisplayRule?.message}
                  />
                )}
            </div>
            {isStaggered && (
              <div>
                <div
                  className="product-compare-preview-summary__price-label-rear"
                  styleName="price-label"
                >
                  Rear
                </div>
                {showMarkdownPrice && (
                  <StackedMarkdownPrice
                    hideInstantPromo
                    product={product?.rearProduct}
                    totalQuantity={product?.totalQuantity}
                  />
                )}
                <div className="display-sm">
                  <Price
                    {...rearPriceInfo}
                    isPriceForEach
                    styleName="price"
                    totalQuantity={product?.totalQuantity}
                    unitName={product?.unitName}
                  />
                </div>
                <div className="hide-sm">
                  <Price
                    {...rearPriceInfo}
                    isPriceForEach
                    seePriceInCartMessage="Price in cart"
                    styleName="price"
                    totalQuantity={product?.totalQuantity}
                    unitName={product?.unitName}
                  />
                </div>
                {showRearMinimumPriceMessage && (
                  <MinimumPriceMessage
                    message={product.rearProduct?.mapDisplayRule?.message}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {!product && (
        <div
          className="product-compare-preview-summary__image-container"
          styleName={hideImage ? 'image-container-full' : 'image-container'}
        >
          <ProductImage
            ariaLabel="Add a tire to compare"
            height={137}
            imageUrl="https://discounttire.scene7.com/is/content/discounttire/product-browse/accessories_tire.svg"
            styleName="placeholder-image"
            width={114}
          />
        </div>
      )}
    </div>
  );
}

ProductComparePreviewSummary.propTypes = {
  className: PropTypes.string,
  hasInstantPromotions: PropTypes.bool,
  hideDescription: PropTypes.bool,
  hideImage: PropTypes.bool,
  hideRemoveButton: PropTypes.bool,
  onClearProduct: PropTypes.func,
  product: PropTypes.object,
  seePriceInCartMessage: PropTypes.string
};

export default ProductComparePreviewSummary;
